import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { AnatomicalSideEnum, ApexSign, AxialRotationEnum, BoneTypeEnum, NextRayMeasuresDeformityAP, NextRayMeasuresDeformityLT, SelectedApexMech, StateDescription, StateTypes, ViewStateType, ViewType } from '@ortho-next/nextray-core';
import { LayerElements, MeasuresRPM, Messages } from '../../../nextray/Models/AppModel';
import { WorkflowRunning } from '../../../nextray/States/State';
import { PrintMeasures } from '../../../nextray/Utils/PrintUtils';
import { Fitbone, LanguageService } from '../../core';
import { CanvasService } from '../../services';



/**
* Base component to share common features in every component.
*/
@Component({
	selector: 'base-component',
	templateUrl: './base.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
})
export class BaseComponent {

	protected readonly langSrv = inject(LanguageService);
	protected readonly canvasSrv = inject(CanvasService);

	labels = computed(() => this.langSrv.labels());


	workflowEnum = WorkflowRunning;
	activeViewEnum = ViewType;
	activeViewStateEnum = ViewStateType;
	stateTypeEnum = StateTypes;
	sideEnum = AnatomicalSideEnum;


	/**
	* Check if current case has AP image.
	*/
	get hasAP(): boolean {
		return this.canvasSrv.hasAP;
	}
	/**
	* Check if current case has Lateral image.
	*/
	get hasLT(): boolean {
		return this.canvasSrv.hasLT;
	}
	/**
	* Check if current case has Long Leg as bone type.
	*/
	get isLongLeg(): boolean {
		return this.canvasSrv.isLongLeg;
	}
	/**
	* Check if current case has Femur as bone type.
	*/
	get isFemur(): boolean {
		return this.canvasSrv.isFemur;
	}
	/**
	* Check if current case has Tibia as bone type.
	*/
	get isTibia(): boolean {
		return this.canvasSrv.isTibia;
	}
	/**
	* Check if current case has left as bone side.
	*/
	get isLeft(): boolean {
		return this.canvasSrv.isLeft;
	}
	/**
	* Check if current case has right as bone side.
	*/
	get isRight(): boolean {
		return this.canvasSrv.isRight;
	}
	/**
	* Get current state description.
	*/
	get stateDescription(): StateDescription | undefined {
		return this.canvasSrv.stateDescription;
	}
	/**
	* Get current state type.
	*/
	get stateType(): StateTypes | undefined {
		return this.canvasSrv.stateType;
	}
	/**
	* Get current state view, i.e AP, Lateral or Multiple.
	*/
	get activeViewState(): ViewStateType | undefined {
		return this.canvasSrv.activeViewState;
	}
	/**
	* Get current active view, i.e AP or Lateral view.
	*/
	get activeView(): ViewType | undefined {
		return this.canvasSrv.activeView;
	}
	/**
	* Get current deformity measures of mechanical axis in AP View.
	*/
	get defAPMeasures(): NextRayMeasuresDeformityAP {
		return this.canvasSrv.defAPMeasures;
	}
	/**
	* Get current deformity measures of mechanical axis in Lateral View.
	*/
	get defLTMeasures(): NextRayMeasuresDeformityLT {
		return this.canvasSrv.defLTMeasures;
	}
	/**
	* Get current deformity measures of contralateral axis.
	*/
	get defCLMeasures(): NextRayMeasuresDeformityAP {
		return this.canvasSrv.defCLMeasures;
	}
	/**
	* Get current scale factor on AP image.
	*/
	get scaleFactorAP(): number | undefined {
		return this.canvasSrv.scaleFactorAP;
	}
	/**
	* Get current scale factor on Lateral image.
	*/
	get scaleFactorLT(): number | undefined {
		return this.canvasSrv.scaleFactorLT;
	}
	/**
	* Get current layers state.
	*/
	get layers(): LayerElements | undefined {
		return this.canvasSrv.layers;
	}
	/**
	* Get current layers values.
	*/
	get layersValue(): LayerElements | undefined {
		return this.canvasSrv.layersValue;
	}
	/**
	* Check if layers component is visible.
	*/
	get layersVisible(): boolean {
		return this.canvasSrv.layersVisible;
	}
	/**
	* Get current correction measures.
	*/
	get rpmMeasures(): MeasuresRPM | undefined {
		return this.canvasSrv.rpmMeasures;
	}
	/**
	* Get correction measures for print report.
	*/
	get printMeasures(): PrintMeasures | undefined {
		return this.canvasSrv.printMeasures;
	}
	/**
	* Get messages visibility.
	*/
	get messages(): Messages | undefined {
		return this.canvasSrv.messages;
	}
	/**
	* Check if eoc cut is active.
	*/
	get isEocCut(): boolean {
		return this.canvasSrv.isEocCut;
	}
	/**
	* Get selected apex.
	*/
	get selectedApex(): SelectedApexMech | undefined {
		return this.canvasSrv.selectedApex;
	}
	/**
	 * Indicates if the fitbone is inserted.
	 */
	public get isFitboneInserted(): boolean {
		return this.canvasSrv.isFitboneInserted;
	}
	/**
	 * Get fitbone data.
	 */
	public get fitbone(): Fitbone | undefined {
		return this.canvasSrv.fitbone;
	}
	/**
	 * Indicates if stroke is locked.
	 */
	public get isStrokeLocked(): boolean {
		return this.canvasSrv.isStrokeLocked;
	}
	/**
	 * Indicates if the insertion point is inserted.
	 */
	public get isInsPointInserted(): boolean {
		return this.canvasSrv.isInsPointInserted;
	}
	/**
	* Get axial roatation value of femur.
	*/
	get def_femur_axialRotation(): number | undefined {
		return this.canvasSrv.def_femur_axialRotation;
	}
	/**
	* Get axial roatation sign of femur.
	*/
	get def_femur_axialRotationSign(): AxialRotationEnum | undefined {
		return this.canvasSrv.def_femur_axialRotationSign;
	}
	/**
	* Get axial roatation value of tibia.
	*/
	get def_tibia_axialRotation(): number | undefined {
		return this.canvasSrv.def_tibia_axialRotation;
	}
	/**
	* Get axial roatation sign of tibia.
	*/
	get def_tibia_axialRotationSign(): AxialRotationEnum | undefined {
		return this.canvasSrv.def_tibia_axialRotationSign;
	}
	/**
	* Get standard angle of LPFA.
	*/
	get def_standardAngles_LPFA(): number | undefined {
		return this.canvasSrv.def_standardAngles_LPFA;
	}
	/**
	* Get standard angle of mLDFA.
	*/
	get def_standardAngles_mLDFA(): number | undefined {
		return this.canvasSrv.def_standardAngles_mLDFA;
	}
	/**
	* Get standard angle of MPTA.
	*/
	get def_standardAngles_MPTA(): number | undefined {
		return this.canvasSrv.def_standardAngles_MPTA;
	}
	/**
	* Get standard angle of LDTA.
	*/
	get def_standardAngles_LDTA(): number | undefined {
		return this.canvasSrv.def_standardAngles_LDTA;
	}
	/**
	* Get standard angle of PPTA.
	*/
	get def_standardAngles_PPTA(): number | undefined {
		return this.canvasSrv.def_standardAngles_PPTA;
	}
	/**
	* Get standard angle of ADTA.
	*/
	get def_standardAngles_ADTA(): number | undefined {
		return this.canvasSrv.def_standardAngles_ADTA;
	}
	/**
	 * Get brightness of AP image.
	 */
	public get brightnessAP(): number | undefined {
		return this.canvasSrv.brightnessAP;
	}
	/**
	 * Get brightness of Lateral image.
	 */
	public get brightnessLT(): number | undefined {
		return this.canvasSrv.brightnessLT;
	}
	/**
	 * Get contrast of AP image.
	 */
	public get contrastAP(): number | undefined {
		return this.canvasSrv.contrastAP;
	}
	/**
	 * Get contrast of Lateral image.
	 */
	public get contrastLT(): number | undefined {
		return this.canvasSrv.contrastLT;
	}
	/**
	 * Gets bone type of Lateral view.
	 */
	public get boneTypeLT(): BoneTypeEnum | undefined {
		return this.canvasSrv.boneTypeLT;
	}
	/**
	 * Indicates if viewer is visible.
	 */
	public get isViewerVisible(): boolean {
		return this.canvasSrv.isViewerVisible;
	}
	/**
	 * Indicates if blocking screw is selected in AP view.
	 */
	public get isBlockingScrewSelectedAP(): boolean {
		return this.canvasSrv.isBlockingScrewSelectedAP;
	}
	/**
	 * Indicates if blocking screw is selected in LT view.
	 */
	public get isBlockingScrewSelectedLT(): boolean {
		return this.canvasSrv.isBlockingScrewSelectedLT;
	}
	/**
	 * Indicates if number of blocking screws is maximum in AP view.
	 */
	public get isBlockingScrewFullAP(): boolean {
		return this.canvasSrv.isBlockingScrewFullAP;
	}
	/**
	 * Indicates if number of blocking screws is maximum in LT view.
	 */
	public get isBlockingScrewFullLT(): boolean {
		return this.canvasSrv.isBlockingScrewFullLT;
	}
	/**
	 * Indicates if measurement tool is selected in AP view.
	 */
	public get isMeasurementToolSelectedAP(): boolean {
		return this.canvasSrv.isMeasurementToolSelectedAP;
	}
	/**
	 * Indicates if measurement tool is selected in LT view.
	 */
	public get isMeasurementToolSelectedLT(): boolean {
		return this.canvasSrv.isMeasurementToolSelectedLT;
	}
	/**
	 * Indicates if app is downloading a fitbone in AP view.
	 */
	public get isFitboneLoadingAP(): boolean {
		return this.canvasSrv.isFitboneLoadingAP;
	}
	/**
	* Indicates if app is downloading a fitbone in lateral view.
	*/
	public get isFitboneLoadingLT(): boolean {
		return this.canvasSrv.isFitboneLoadingLT;
	}
	/**
	 * Check if undo action is enabled.
	 */
	public get isUndoEnabled(): boolean {
		return this.canvasSrv.isUndoEnabled;
	}
	/**
	 * Check if redo action is enabled.
	 */
	public get isRedoEnabled(): boolean {
		return this.canvasSrv.isRedoEnabled;
	}

	/**
	 * Map label for apex sign.
	 */
	apexSignLabel(sign?: string): string {
		switch (sign) {
			case ApexSign[ApexSign.varus]: return this.labels().APEX_SIGN_VARUS;
			case ApexSign[ApexSign.valgus]: return this.labels().APEX_SIGN_VALGUS;
			default: return sign ?? '';
		}
	}

	/**
	 * Map label for axial rotation sign.
	 */
	axialRotSignLabel(sign?: AxialRotationEnum): string {
		switch (sign) {
			case AxialRotationEnum.external: return this.labels().AXIAL_ROT_SIGN_EXTERNAL;
			case AxialRotationEnum.internal: return this.labels().AXIAL_ROT_SIGN_INTERNAL;
			default: return sign ?? '';
		}
	}

}
